<script setup lang="ts">
  import { capitalize } from 'es-toolkit';
  import { uniqueId } from 'es-toolkit/compat';
  import { computed, ref, useCssModule, useSlots } from 'vue';

  import type { StatusColor, StatusIcon, StatusSeverity } from '../../../types/statusLevels';
  import { StatusColors, StatusIcons } from '../../../types/statusLevels';
  import vSticky from '../../directives/sticky/sticky';
  import Icon from '../Icon/Icon.vue';

  defineOptions({
    name: 'll-alert',
  });

  export interface AlertProps {
    /**
     * Determines if the alert is centered
     */
    centered?: boolean;

    /**
     * Determines if the alert is permanent (doesn't have a close button).
     */
    permanent?: boolean;

    /**
     * The type of border radius to use. Available types:
     *
     * - `none` - no border radius
     * - `standard` - 4px border radius
     */
    radius?: 'none' | 'standard';

    /**
     * The type of alert. Available types:
     *
     * - `info`
     * - `success`
     * - `warning`
     * - `error`
     */
    severity?: StatusSeverity;

    /**
     * Determines if the alert is sticky. By default, it's not sticky. The provided `number` value is a top offset of alert.
     */
    sticky?: number | null;

    /**
     * The variant of alert. Available variants:
     *
     * - `filled` - previously alert component
     * - `outlined` - previously banner component
     */
    variant?: 'outlined' | 'filled';
  }

  const props = withDefaults(defineProps<AlertProps>(), {
    centered: false,
    permanent: false,
    radius: 'standard',
    severity: 'info',
    sticky: null,
    variant: 'outlined',
  });

  const iconId = uniqueId('close-icon');
  const slots = useSlots();
  const classes = useCssModule();
  const show = ref(true);
  const icon = computed<StatusIcon>(() => StatusIcons[capitalize(props.severity)]);
  const color = computed<StatusColor>(() => StatusColors[capitalize(props.severity)]);

  const iconColor = computed<string>(() => {
    if (props.variant === 'filled' && ['warning', 'success'].includes(props.severity)) {
      return 'text-ice-900';
    }

    if (props.variant === 'filled' && ['info', 'error'].includes(props.severity)) {
      return 'text-white';
    }

    return `text-${color.value}`;
  });

  const computedTextColor = computed<string>(() => {
    if (props.variant === 'filled' && ['info', 'error'].includes(props.severity)) {
      return 'text-white';
    }

    return 'text-ice-900';
  });
</script>

<template>
  <div
    v-if="show"
    v-sticky="props.sticky"
    class="stash-alert relative flex items-center border border-solid p-3 leading-6"
    :class="[
      `stash-alert--${props.severity}`,
      `stash-alert--${props.variant}`,
      computedTextColor,
      {
        'my-6': props.variant === 'outlined', // Todo - this makes ZERO sense. We should remove this altogether and let the consumer handle the margin.
        rounded: props.radius === 'standard',
        'border-red-500': props.severity === 'error',
        'border-orange-500': props.severity === 'warning',
        'border-blue-500': props.severity === 'info',
        'border-green-500': props.severity === 'success',
        'bg-red-500': props.severity === 'error' && props.variant === 'filled',
        'bg-orange-500': props.severity === 'warning' && props.variant === 'filled',
        'bg-blue-500': props.severity === 'info' && props.variant === 'filled',
        'bg-green-500': props.severity === 'success' && props.variant === 'filled',
        'bg-red-100': props.severity === 'error' && props.variant === 'outlined',
        'bg-orange-100': props.severity === 'warning' && props.variant === 'outlined',
        'bg-blue-100': props.severity === 'info' && props.variant === 'outlined',
        'bg-green-100': props.severity === 'success' && props.variant === 'outlined',
      },
    ]"
    data-test="stash-alert"
  >
    <Icon
      class="stash-alert__icon"
      :class="[classes.iconInfo, iconColor, `stash-alert__icon--${icon}`]"
      :name="icon"
      :title="`${props.severity} icon`"
      data-test="stash-alert|status-icon"
    />

    <!-- Alert text elements -->
    <div
      class="flex flex-1 flex-wrap px-2"
      :class="{
        'justify-center': props.centered,
        'justify-between': !props.centered,
      }"
    >
      <p class="m-0">
        <slot></slot>
      </p>

      <div
        v-if="slots.link"
        class="stash-alert__link cursor-pointer underline hover:no-underline"
        :class="[classes.link, computedTextColor, { 'ml-2': props.centered }]"
        data-test="stash-alert|link"
      >
        <slot name="link"></slot>
      </div>
    </div>

    <button
      v-if="!props.permanent"
      class="stash-alert__close"
      :class="[classes.button, computedTextColor]"
      type="button"
      data-test="stash-alert|close"
      @click="show = false"
    >
      <Icon :id="iconId" name="close" title="Dismiss alert" />
    </button>
  </div>
</template>

<style module>
  :global(.stash-alert__link) {
    a,
    button {
      color: var(--color-ice-900);
      cursor: pointer;
      text-decoration: underline;

      &:hover {
        text-decoration: none;
      }
    }
  }

  :global(.stash-alert--filled.stash-alert--error),
  :global(.stash-alert--filled.stash-alert--info) {
    :global(.stash-alert__link) a,
    :global(.stash-alert__link) button {
      color: var(--color-white);
    }
  }
</style>
